import type { Alignment, CompareMode, Grain } from "@kundeninfo/api-client";
import { formatDatum } from "../../format.js";
import { tageZwischen } from "../tickets/ticketFormat.js";
import { addDays, dayMs } from "../tickets/timelineModel.js";
import { COCKPIT_TEXT_WEB } from "./cockpitTexts.js";

/**
 * ZEITRAUM DES COCKPITS IN DER OBERFLÄCHE. Das Web darf die Domäne nicht importieren: `grainOf`, `defaultAlignmentOf` und
 * `compareRangeOf` spiegeln `grainFor`, `defaultAlignment` und `compareRange` aus `packages/domain/src/cockpitRules.ts` (K21), die
 * Beispieltabelle `COMPARE_CASES` steht wörtlich in beiden Testdateien. Ändert sich dort eine Regel, ändert sie sich hier. So steht
 * der Vergleichstext auch, solange keine Kachel geladen ist (Ruling 6). Alle Tage sind Berliner Kalendertage als ISO-Zeichenkette,
 * gerechnet wird über `Date.UTC`; eine örtliche Zeitzone kommt nicht vor.
 */

export type PeriodKind = "MONTH" | "QUARTER" | "YEAR" | "FREE";
/** `back`: der Zeitraum, aus dem man hineingeklickt hat. */
export type Period = { kind: PeriodKind; from: string; to: string; back: Period | null };
type DayRange = { from: string; to: string };

export const RANGE_MAX_DAYS = 1096;
/** Kalendertage, mit denen die Zeitrechnung gilt (`COCKPIT_MIN_DAY`, `COCKPIT_MAX_DAY`). */
const MIN_DAY = "1900-01-01", MAX_DAY = "2999-12-31";

const T = COCKPIT_TEXT_WEB.period;
const isoOf = (ms: number): string => new Date(ms).toISOString().slice(0, 10);
const yearOf = (day: string): string => day.slice(0, 4);
const monthOf = (day: string): number => Number(day.slice(5, 7));
const firstOfMonth = (day: string): string => `${day.slice(0, 8)}01`;
const rangeDays = (r: DayRange): number => tageZwischen(r.from, r.to) + 1;
const isDay = (day: string): boolean =>
  /^\d{4}-\d{2}-\d{2}$/.test(day) && day >= MIN_DAY && day <= MAX_DAY && addDays(day, 0) === day;

/** Letzter Tag des Monats (Spiegel von `monatsLetzter`). */
function monthLast(day: string): string {
  const d = new Date(dayMs(day));
  return isoOf(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
}

/** Derselbe Tag n Monate weiter, fehlt er dort: der Monatsletzte (Spiegel von `plusMonate`). */
function plusMonths(day: string, n: number): string {
  const d = new Date(dayMs(day));
  const year = d.getUTCFullYear(), month = d.getUTCMonth() + n;
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return isoOf(Date.UTC(year, month, Math.min(d.getUTCDate(), last)));
}

// ── Raster und Vergleich: Spiegel von cockpitRules.ts ────────────────────────────────────────────────────────────

/** Bis 62 Tage DAY, bis 186 WEEK, darüber MONTH (`grainFor`). */
export function grainOf(r: DayRange): Grain {
  const l = rangeDays(r);
  return l <= 62 ? "DAY" : l <= 186 ? "WEEK" : "MONTH";
}

/** Tag und Woche wochentaggleich, Monat kalendergleich (`defaultAlignment`, me-design 72). */
export const defaultAlignmentOf = (g: Grain): Alignment => (g === "MONTH" ? "CALENDAR" : "WEEKDAY");

const shift = (r: DayRange, days: number): DayRange => ({ from: addDays(r.from, days), to: addDays(r.to, days) });
const monthIndex = (day: string): number => Number(yearOf(day)) * 12 + monthOf(day);

/** Vergleichszeitraum (`compareRange`). Ganze Monate: `from` Monatserster, `to` Monatsletzter. */
export function compareRangeOf(r: DayRange, mode: CompareMode, align: Alignment): DayRange {
  const l = rangeDays(r);
  const wholeMonths = r.from.endsWith("-01") && r.to === monthLast(r.to);
  if (align === "WEEKDAY") return shift(r, mode === "PREVIOUS_YEAR" ? -364 : -7 * Math.ceil(l / 7));
  if (mode === "PREVIOUS_YEAR") {
    const to = plusMonths(r.to, -12);
    return { from: plusMonths(r.from, -12), to: wholeMonths ? monthLast(to) : to };
  }
  if (!wholeMonths) return shift(r, -l);
  return { from: plusMonths(r.from, -(monthIndex(r.to) - monthIndex(r.from) + 1)), to: addDays(r.from, -1) };
}

// ── Zeitraum ─────────────────────────────────────────────────────────────────────────────────────────────────────

const PRESET_MONTHS: Readonly<Record<Exclude<PeriodKind, "FREE">, number>> = { MONTH: 1, QUARTER: 3, YEAR: 12 };

/** Der volle Monat, das volle Quartal bzw. das volle Jahr um den Tag. */
export function periodAround(kind: Exclude<PeriodKind, "FREE">, day: string): Period {
  const size = PRESET_MONTHS[kind];
  const first = plusMonths(firstOfMonth(day), -((monthOf(day) - 1) % size));
  return { kind, from: first, to: monthLast(plusMonths(first, size - 1)), back: null };
}

/** Eine Einheit weiter oder zurück, „frei“ um seine Länge; der Rückweg entfällt. */
export function shiftPeriod(p: Period, steps: -1 | 1): Period {
  if (p.kind === "FREE") return { kind: "FREE", ...shift(p, steps * rangeDays(p)), back: null };
  return periodAround(p.kind, plusMonths(p.from, steps * PRESET_MONTHS[p.kind]));
}

/** Voreinstellungen um heute; „frei“ behält seine Länge L und legt sich um heute: `from = heute − ⌊(L − 1)/2⌋` (K35). */
export function toToday(p: Period, today: string): Period {
  if (p.kind !== "FREE") return periodAround(p.kind, today);
  const l = rangeDays(p);
  const from = addDays(today, -Math.floor((l - 1) / 2));
  return { kind: "FREE", from, to: addDays(from, l - 1), back: null };
}

/** Der freie Zeitraum; `null` ohne zwei Kalendertage oder außerhalb von 1 bis 1 096 Tagen (wie `validateCockpitRange`). */
export function freePeriod(from: string, to: string): Period | null {
  if (!isDay(from) || !isDay(to)) return null;
  const l = rangeDays({ from, to });
  return l >= 1 && l <= RANGE_MAX_DAYS ? { kind: "FREE", from, to, back: null } : null;
}

/** Hineinklicken (E53): Nur ein Monatsabschnitt wählt seinen ganzen Monat, mit Rückweg; Tag und Woche bleiben ohne Wirkung. */
export function drillInto(p: Period, bucketStart: string, grain: Grain): Period | null {
  return grain === "MONTH" ? { ...periodAround("MONTH", bucketStart), back: p } : null;
}

/** Steht eine Voreinstellung ohne Hineinklicken auf dem laufenden Abschnitt? Dann rückt sie nach Mitternacht mit heute weiter (K31). */
export const isCurrent = (p: Period, today: string): boolean =>
  p.kind !== "FREE" && p.back === null && p.from <= today && today <= p.to;

// ── Vergleichswahl (651) ─────────────────────────────────────────────────────────────────────────────────────────

/** `align: null` heißt: keine bewusste Wahl, die Ausrichtung folgt dem Raster. */
export type CompareChoice = { mode: "NONE" | CompareMode; align: Alignment | null };

export const effectiveAlignment = (c: CompareChoice, g: Grain): Alignment => c.align ?? defaultAlignmentOf(g);

/** Wer die Vorgabe des Rasters wählt, nimmt seine bewusste Wahl zurück (651). */
export const chooseAlignment = (c: CompareChoice, chosen: Alignment, g: Grain): CompareChoice =>
  ({ ...c, align: chosen === defaultAlignmentOf(g) ? null : chosen });

// ── Texte (Ruling 6) ─────────────────────────────────────────────────────────────────────────────────────────────

/** „27.08.–26.09.2026“, über den Jahreswechsel „28.12.2025–05.01.2026“. */
export function rangeText(r: DayRange): string {
  const from = formatDatum(r.from);
  return T.range(yearOf(r.from) === yearOf(r.to) ? from.slice(0, 6) : from, formatDatum(r.to));
}

/** „05.10.–18.10.26“, über den Jahreswechsel „28.12.25–05.01.26“. */
function rangeTextShort(r: DayRange): string {
  const two = (day: string): string => { const d = formatDatum(day); return `${d.slice(0, 6)}${d.slice(8)}`; };
  return T.range(yearOf(r.from) === yearOf(r.to) ? formatDatum(r.from).slice(0, 6) : two(r.from), two(r.to));
}

function presetName(kind: Exclude<PeriodKind, "FREE">, from: string, phone: boolean): string {
  const year = yearOf(from), month = monthOf(from);
  switch (kind) {
    case "MONTH": return T.monthName((phone ? T.monthsShort : T.months)[month - 1]!, year);
    case "QUARTER": return T.quarterName(Math.ceil(month / 3), year);
    case "YEAR": return year;
  }
}

/** Fenstertext: „Oktober 2026 · 01.10.–31.10.2026“, am Telefon „Okt 2026“. */
export function periodText(p: Period, phone: boolean): string {
  if (p.kind === "FREE") return phone ? rangeTextShort(p) : T.freeWindow(rangeText(p), rangeDays(p));
  const name = presetName(p.kind, p.from, phone);
  return phone ? name : T.window(name, rangeText(p));
}

/** „Vergleich 27.08.–26.09.2026 · wochentaggleich“; ohne Vergleich leer. Unabhängig von jeder Kachel (Ruling 6). */
export function compareText(p: Period, c: CompareChoice): string {
  if (c.mode === "NONE") return "";
  const align = effectiveAlignment(c, grainOf(p));
  return T.compare(rangeText(compareRangeOf(p, c.mode, align)), COCKPIT_TEXT_WEB.toolbar.alignments[align]);
}
